import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Printer } from 'lucide-react';
import { API } from '../api';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import Menu from '../ui/Menu';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import Loading from '../ui/Loading';
import EntityLink from '../ui/EntityLink';
import { StatusBadge } from '../ui/Badge';
import useAsync from '../ui/useAsync';
import useUrlFilters from '../ui/useUrlFilters';
import { useToast } from '../ui/Toast';
import { formatCount, formatDate, plural, teamLabel } from '../ui/format';
import { ownershipPath, ticketPath } from '../ui/entityRoutes';
import { TEAM_OPTIONS, ticketName } from './ticketFields';
import { DailyBars, DailyLine } from './DashboardCharts';
import { DAYS, formatShare, share, ticketSummary, valueForTeam } from './dashboardData';
import openDashboardReport from './dashboardReport';

const CHART_COLORS = { bar: 'rgb(var(--signal))', line: 'rgb(var(--ink))', rule: 'rgb(var(--rule))', muted: 'rgb(var(--graphite))' };
const DAY_MS = 24 * 60 * 60 * 1000;

const muted = (text) => <span className="font-normal text-graphite">{text}</span>;

function Band({ id, title, note, children }) {
  return (
    <section aria-labelledby={id} className="grid grid-cols-[160px_minmax(0,1fr)] gap-x-6 py-6 border-t border-rule first-of-type:border-t-0 first-of-type:pt-2">
      <div>
        <h2 id={id} className="text-section text-ink">{title}</h2>
        <p className="mt-0.5 text-small text-graphite">{note}</p>
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

function ColumnHeading({ children, aside }) {
  return (
    <h3 className="flex items-baseline gap-2 mb-1.5 text-body font-semibold text-graphite">
      {children}
      {aside && <span className="ml-auto text-small font-normal">{aside}</span>}
    </h3>
  );
}

// rows: [{ label, value }]; a row with value undefined is left out.
function KeyValues({ rows }) {
  return (
    <ul className="m-0 p-0 list-none">
      {rows.filter((r) => r.value !== undefined).map((r) => (
        <li key={r.label} className="flex items-baseline justify-between gap-3 min-h-9 py-1.5 border-b border-rule last:border-b-0">
          <span className="text-ink">{r.label}</span>
          <span className="min-w-0 text-right font-semibold text-ink tabular-nums">{r.value}</span>
        </li>
      ))}
    </ul>
  );
}

const countOrDash = (n) => (n === null || n === undefined ? muted('—') : formatCount(n));

// ── Exposure ────────────────────────────────────────────────────────────────

function SeverityTotal({ to, count, label, tone }) {
  const color = { crit: 'text-crit', high: 'text-high' }[tone] || 'text-ink';
  const dot = { crit: 'bg-crit', high: 'bg-high', rest: 'bg-low/45' }[tone];
  const body = (
    <>
      <span className={`block text-[26px] leading-8 font-semibold tabular-nums ${color}`}>{formatCount(count)}</span>
      <span className="flex items-center gap-1.5 text-small text-graphite">
        {dot && <span className={`w-2.5 h-2.5 rounded-[2px] ${dot}`} aria-hidden="true" />}
        <span className={to ? 'group-hover:underline' : ''}>{label}</span>
      </span>
    </>
  );
  if (!to) return <div>{body}</div>;
  return <Link to={to} className="group block rounded-control">{body}</Link>;
}

function Fact({ label, to, children }) {
  return (
    <div>
      <dt className="text-small text-graphite">{label}</dt>
      <dd className="font-semibold tabular-nums">
        {to ? <Link to={to} className="text-signal hover:underline">{children}</Link> : children}
      </dd>
    </div>
  );
}

function Exposure({ stats, team }) {
  const total = stats.total_vulnerabilities || 0;
  const critical = stats.critical_vulnerabilities || 0;
  const high = stats.high_vulnerabilities || 0;
  const rest = Math.max(0, total - critical - high);

  if (!stats.total_hosts) {
    return (
      <EmptyState className="!p-0" title={team ? `No active hosts belong to ${teamLabel(team)}` : 'No active hosts yet'}>
        {team ? (
          <>
            Hosts get a team in <Link to={ownershipPath()} className="text-signal hover:underline">Host ownership</Link>. Pick another team or All to see their findings.
          </>
        ) : (
          'Hosts appear once the exporter has sent their first inventory.'
        )}
      </EmptyState>
    );
  }

  return (
    <>
      {total > 0 ? (
        <>
          <div className="flex h-3.5 max-w-[760px] rounded-badge overflow-hidden bg-rule" aria-hidden="true">
            <span className="bg-crit" style={{ flexGrow: critical }} />
            <span className="bg-high" style={{ flexGrow: high }} />
            <span className="bg-low/45" style={{ flexGrow: rest }} />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-10 gap-y-3">
            <SeverityTotal to="/vulnerabilities" count={total} label="Vulnerability findings" />
            <SeverityTotal to="/vulnerabilities?severity=Critical" count={critical} label="Critical" tone="crit" />
            <SeverityTotal to="/vulnerabilities?severity=High" count={high} label="High" tone="high" />
            <SeverityTotal count={rest} label="Medium and low" tone="rest" />
          </div>
        </>
      ) : (
        <p className="text-body text-ink">No open vulnerability findings{team ? ` on ${teamLabel(team)} hosts` : ''}.</p>
      )}
      <dl className="mt-5 flex flex-wrap gap-x-10 gap-y-3">
        <Fact label="Hosts affected" to="/hosts">
          {formatCount(stats.hosts_with_vulnerabilities)} of {plural(stats.total_hosts, 'host')}
        </Fact>
        <Fact label="Vulnerable packages" to="/packages?mode=with_vulns">
          {formatCount(stats.packages_with_vulnerabilities)} of {formatCount(stats.total_packages)}
        </Fact>
        <Fact label="Outdated packages" to="/packages?outdated=outdated">{formatCount(stats.outdated_packages)}</Fact>
        <Fact label="All packages" to="/packages">{formatCount(stats.total_packages)}</Fact>
      </dl>
    </>
  );
}

// ── Hotspots ────────────────────────────────────────────────────────────────

const findings = (n) => <span className="ml-2 text-small font-normal text-graphite">{plural(n, 'finding')}</span>;

// "Most tickets" values are read when the API sends them; it doesn't today.
const mostTickets = (value, link) => (value === undefined ? undefined : value ? link(value) : muted('None yet'));

function Hotspots({ stats }) {
  const host = stats.host_with_most_vulnerabilities;
  const pkg = stats.package_with_most_vulnerabilities;
  const total = stats.total_vulnerabilities || 0;
  const packages = stats.total_packages || 0;

  return (
    <div className="grid grid-cols-3 gap-8">
      <div>
        <ColumnHeading>Hosts</ColumnHeading>
        <KeyValues
          rows={[
            { label: 'Hosts with vulnerabilities', value: formatCount(stats.hosts_with_vulnerabilities) },
            {
              label: 'Most vulnerable host',
              value: host ? <><EntityLink kind="host" id={host.host_id} name={host.hostname} />{findings(host.vuln_count)}</> : muted('None'),
            },
            { label: 'Hosts in tickets', value: countOrDash(stats.hosts_in_tickets) },
            { label: 'Host with most tickets', value: mostTickets(stats.host_with_most_tickets, (name) => <EntityLink kind="host" name={name} />) },
          ]}
        />
      </div>
      <div>
        <ColumnHeading>Packages</ColumnHeading>
        <KeyValues
          rows={[
            { label: 'Vulnerable share', value: formatShare(share(stats.packages_with_vulnerabilities, packages)) },
            { label: 'Outdated share', value: formatShare(share(stats.outdated_packages, packages)) },
            {
              label: 'Most vulnerable package',
              value: pkg ? <><EntityLink kind="package" id={pkg.package_id} name={pkg.package_name} />{findings(pkg.vuln_count)}</> : muted('None'),
            },
            { label: 'Packages in tickets', value: countOrDash(stats.packages_in_tickets) },
            { label: 'Package with most tickets', value: mostTickets(stats.package_with_most_tickets, (name) => <EntityLink kind="package" name={name} />) },
          ]}
        />
      </div>
      <div>
        <ColumnHeading>Vulnerabilities</ColumnHeading>
        <KeyValues
          rows={[
            { label: 'Critical share', value: formatShare(share(stats.critical_vulnerabilities, total)) },
            { label: 'High share', value: formatShare(share(stats.high_vulnerabilities, total)) },
            { label: 'Vulnerabilities in tickets', value: countOrDash(stats.vulns_in_tickets) },
            { label: 'Vulnerability with most tickets', value: mostTickets(stats.vuln_with_most_tickets, (cve) => <EntityLink kind="cve" cve={cve} />) },
          ]}
        />
      </div>
    </div>
  );
}

// ── Remediation ─────────────────────────────────────────────────────────────

function StatusCounts({ summary }) {
  const lastMonth = new Map(summary.byStatusLastMonth.map((r) => [r.status.toLowerCase(), r.count]));
  const cell = 'h-9 px-0 border-b border-rule tabular-nums';
  return (
    <table className="w-full border-collapse text-body">
      <thead>
        <tr>
          <th scope="col" className="sr-only">Status</th>
          <th scope="col" className={`${cell} w-16 text-right text-small font-semibold text-graphite`}>All</th>
          <th scope="col" className={`${cell} w-24 text-right text-small font-semibold text-graphite`}>Last {DAYS} days</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row" className={`${cell} text-left font-semibold`}>Total</th>
          <td className={`${cell} text-right font-semibold`}>{formatCount(summary.total)}</td>
          <td className={`${cell} text-right font-semibold`}>{formatCount(summary.totalLastMonth)}</td>
        </tr>
        {summary.byStatus.map((r) => (
          <tr key={r.status}>
            <th scope="row" className={`${cell} text-left font-normal`}><StatusBadge status={r.status} /></th>
            <td className={`${cell} text-right`}>{r.count ? formatCount(r.count) : muted('0')}</td>
            <td className={`${cell} text-right`}>{lastMonth.get(r.status.toLowerCase()) ? formatCount(lastMonth.get(r.status.toLowerCase())) : muted('0')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const daysOpen = (ticket, now) => Math.max(0, Math.floor((now - new Date(ticket.created_at || 0).getTime()) / DAY_MS));

function TicketList({ tickets, empty, showAge = false }) {
  const now = Date.now();
  if (tickets.length === 0) return <p className="py-2 text-body text-graphite">{empty}</p>;
  return (
    <ul className="m-0 p-0 list-none">
      {tickets.map((t) => (
        <li key={t.id} className="py-2 border-b border-rule last:border-b-0">
          <div className="flex items-baseline gap-3">
            <Link to={ticketPath(t.id)} className="min-w-0 truncate font-mono text-id text-signal hover:underline" title={ticketName(t)}>
              {ticketName(t)}
            </Link>
            <span className="ml-auto shrink-0 text-small text-graphite tabular-nums">{formatDate(t.created_at) || '—'}</span>
          </div>
          <div className="mt-1 flex items-center gap-2.5 text-small text-graphite">
            <StatusBadge status={t.status} />
            {teamLabel(t.flow_type) && <span>{teamLabel(t.flow_type)}</span>}
            {t.assignee && <span className="min-w-0 truncate">{t.assignee}</span>}
            {showAge && <span className="ml-auto shrink-0 tabular-nums">{daysOpen(t, now) ? `Open ${plural(daysOpen(t, now), 'day')}` : 'Opened today'}</span>}
          </div>
        </li>
      ))}
    </ul>
  );
}

// Per-team figures the legacy page read from the statistics response. The API doesn't send them
// today; each block appears once it does.
function OptionalFigures({ stats, team }) {
  const days = (n) => (n === null || n === undefined ? muted('—') : `${Number(n).toFixed(1)} days`);
  const perTeam = (globalValue, map) => {
    if (globalValue === undefined && map === undefined) return null;
    if (team) return [{ label: teamLabel(team), value: days(valueForTeam(map, team)) }];
    return [{ label: 'All teams', value: days(globalValue) }, ...Object.entries(map || {}).map(([flow, v]) => ({ label: teamLabel(flow), value: days(v) }))];
  };
  const blocks = [
    ['Detection to ticket', perTeam(stats.avg_ticket_creation_days_global, stats.avg_ticket_creation_days_by_flow)],
    ['Ticket to resolution', perTeam(stats.avg_ticket_resolve_days_global, stats.avg_ticket_resolve_days_by_flow)],
    ['Scheduled ticket creation', stats.ticket_creation_schedule && (team
      ? [{ label: teamLabel(team), value: valueForTeam(stats.ticket_creation_schedule, team) || muted('Not set') }]
      : Object.entries(stats.ticket_creation_schedule).map(([k, v]) => ({ label: teamLabel(k), value: v || muted('Not set') })))],
    ['Severity of ticketed findings, last 30 days', stats.ticket_status_last_month && Object.entries(stats.ticket_status_last_month).map(([k, v]) => ({ label: k, value: formatCount(v) }))],
  ].filter(([, rows]) => rows);
  if (blocks.length === 0) return null;
  return (
    <div className="mt-8 grid grid-cols-3 gap-8">
      {blocks.map(([title, rows]) => (
        <div key={title}>
          <ColumnHeading>{title}</ColumnHeading>
          <KeyValues rows={rows} />
        </div>
      ))}
    </div>
  );
}

function Remediation({ tickets, summary, team, stats }) {
  if (tickets.loading) return <Loading label="Loading tickets" className="!px-0 !py-2" />;
  if (tickets.error) return <ErrorState className="!px-0 !py-2" title="Tickets could not be loaded" error={tickets.error} onRetry={tickets.retry} />;
  if (summary.total === 0) {
    return (
      <>
        <EmptyState className="!p-0" title={team ? `No tickets for ${teamLabel(team)} yet` : 'No tickets yet'} action={<Button to="/tickets">Go to Tickets</Button>}>
          Tickets created on the Tickets page or from a CVE or package page show up here with their status.
        </EmptyState>
        <OptionalFigures stats={stats} team={team} />
      </>
    );
  }

  const fetched = tickets.data?.data?.length ?? 0;
  const capped = tickets.data?.total > fetched;
  const pendingNow = summary.pendingDaily[summary.pendingDaily.length - 1]?.count ?? 0;

  return (
    <>
      <div className="grid grid-cols-3 gap-8">
        <div>
          <ColumnHeading>Ticket status</ColumnHeading>
          <StatusCounts summary={summary} />
          {capped && <p className="mt-2 text-small text-graphite">Counted from the newest {formatCount(fetched)} of {formatCount(tickets.data.total)} tickets.</p>}
        </div>
        <div>
          <ColumnHeading aside={<Link to="/tickets" className="text-signal hover:underline">All tickets</Link>}>Recent tickets</ColumnHeading>
          <TicketList tickets={summary.recent} empty="No tickets yet." />
        </div>
        <div>
          <ColumnHeading>Oldest pending</ColumnHeading>
          <TicketList tickets={summary.oldestPending} empty="Nothing is waiting: every ticket is resolved or canceled." showAge />
        </div>
      </div>
      <div className="mt-8 grid grid-cols-2 gap-8">
        <div>
          <ColumnHeading aside="Select a day to list its tickets">Opened per day</ColumnHeading>
          <DailyBars
            data={summary.opened}
            colors={CHART_COLORS}
            label={`Tickets opened per day, last ${DAYS} days`}
            linkFor={(d) => `/tickets?created=${d.date}`}
          />
        </div>
        <div>
          <ColumnHeading aside={`${formatCount(pendingNow)} pending now`}>Pending at end of day</ColumnHeading>
          <DailyLine data={summary.pendingDaily} colors={CHART_COLORS} label={`Tickets in Created or Waiting for approval at the end of each day, last ${DAYS} days`} />
        </div>
      </div>
      <OptionalFigures stats={stats} team={team} />
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

const TEAMS = [{ value: '', label: 'All' }, ...TEAM_OPTIONS];

function TeamMenu({ value, onChange }) {
  const items = TEAMS.map((t) => ({ key: t.value || 'all', label: t.label, type: 'radio', checked: t.value === value, onSelect: () => onChange(t.value) }));
  return (
    <Menu items={items} label="Team filter" align="end" triggerClassName={value ? 'border-signal' : ''}>
      <span className="text-graphite">Team:</span>
      <span className={value ? 'font-semibold text-ink' : 'text-ink'}>{TEAMS.find((t) => t.value === value)?.label || value}</span>
      <ChevronDown size={14} className="text-graphite" aria-hidden="true" />
    </Menu>
  );
}

function BandsSkeleton() {
  const bar = (w, h = 'h-3') => <div className={`${h} rounded-badge bg-rule skeleton`} style={{ width: w }} />;
  return (
    <div aria-busy="true" aria-label="Loading statistics">
      <Band id="exposure-loading" title="Exposure" note="Open findings by severity">
        <div className="max-w-[760px]">{bar('100%', 'h-3.5')}</div>
        <div className="mt-4 flex gap-10">{[56, 40, 44, 72].map((w) => <div key={w} className="space-y-2">{bar(w, 'h-6')}{bar(w + 24)}</div>)}</div>
      </Band>
      <Band id="hotspots-loading" title="Hotspots" note="Where findings concentrate">
        <div className="grid grid-cols-3 gap-8">{[0, 1, 2].map((i) => <div key={i} className="space-y-4">{[1, 2, 3, 4].map((r) => <div key={r}>{bar(`${50 + ((i + r) % 3) * 15}%`)}</div>)}</div>)}</div>
      </Band>
    </div>
  );
}

export default function Dashboard() {
  const toast = useToast();
  const [filters, setFilters] = useUrlFilters({ team: '' });
  const team = filters.team;

  const stats = useAsync(async () => {
    const data = await API.GET_DASHBOARD_STATISTICS(team || undefined);
    if (!data) throw new Error('');
    return data;
  }, `stats|${team}`);
  const tickets = useAsync(() => API.GET_TICKETS(), 'tickets');
  const ticketRows = tickets.data?.data || tickets.data?.tickets;
  const summary = useMemo(() => (ticketRows ? ticketSummary(ticketRows, team) : null), [ticketRows, team]);

  const exportPdf = () => {
    if (!openDashboardReport({ stats: stats.data, team, tickets: summary })) {
      toast.error('The report window was blocked. Allow pop-ups for this site, then export again.');
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-6 pt-5 pb-10 max-w-[1320px]">
        <PageHeader
          title="Dashboard"
          className="!mb-2"
          actions={(
            <>
              <TeamMenu value={team} onChange={(next) => setFilters({ team: next })} />
              <Button onClick={exportPdf} disabled={!stats.data}>
                <Printer size={15} strokeWidth={1.75} className="text-graphite" aria-hidden="true" />
                Export PDF
              </Button>
            </>
          )}
        />
        {stats.loading && <BandsSkeleton />}
        {stats.error && <ErrorState title="Dashboard statistics could not be loaded" error={stats.error.message ? stats.error : null} onRetry={stats.retry} />}
        {stats.data && (
          <>
            <Band id="exposure" title="Exposure" note={team ? `Open findings on ${teamLabel(team)} hosts` : 'Open findings by severity'}>
              <Exposure stats={stats.data} team={team} />
            </Band>
            <Band id="hotspots" title="Hotspots" note="Where findings concentrate">
              <Hotspots stats={stats.data} />
            </Band>
            <Band id="remediation" title="Remediation" note="Ticket progress">
              <Remediation tickets={tickets} summary={summary} team={team} stats={stats.data} />
            </Band>
          </>
        )}
      </div>
    </div>
  );
}
